import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React from 'react';

import type { Account } from '@feathr/blackbox';

import { CsmCell, FavoriteCell, LogoCell, NameCell, PackageCell } from './cells';

/**
 * Columns for the accounts table. `name` and `license.package` are server sort fields, so their
 * column `id` is the field the backend orders by; the accessor value itself is unused (sorting is
 * manual) but TanStack requires an accessor before it will make a header sortable.
 *
 * Every cell body is its own observer component. Cell callbacks are invoked inside
 * `TanStackTable`'s body rather than this page's render, so a `get()` here would not be tracked by
 * the page's own `observer` and the cell would never re-render.
 */
function accountsColumns(t: TFunction): Array<ColumnDef<Account>> {
  return [
    {
      cell: ({ row }: CellContext<Account, unknown>): JSX.Element => (
        <FavoriteCell original={row.original} />
      ),
      enableSorting: false,
      header: t('Favorite'),
      id: 'favorite',
      meta: { align: 'center' },
      size: 80,
    },
    {
      cell: ({ row }: CellContext<Account, unknown>): JSX.Element => (
        <LogoCell original={row.original} />
      ),
      enableSorting: false,
      header: t('Logo'),
      id: 'logo',
      meta: { align: 'center' },
      size: 80,
    },
    {
      accessorFn: (row): string => row.name,
      cell: ({ row }: CellContext<Account, unknown>): JSX.Element => (
        <NameCell original={row.original} />
      ),
      header: t('Account'),
      id: 'name',
      /*
       * `isFlex` gives this column the width the others don't use, so the table fills its
       * container.
       *
       * `isPrimary` is here for the column it keeps the 150-350px name range *off*: that range
       * otherwise lands on whichever column comes first, which here is the star toggle, flooring
       * an 81px icon column at 150px. Naming this column primary is also simply true — it is the
       * one identifying the row.
       */
      meta: { isFlex: true, isPrimary: true },
      // Ascending on the first header click; TanStack would otherwise start descending.
      sortDescFirst: false,
    },
    {
      // Sort field is the column `id` (`license.package`); this value only satisfies TanStack's
      // requirement that a sortable column have an accessor.
      accessorFn: (row): string => row.activePackage?.name ?? '',
      cell: ({ row }: CellContext<Account, unknown>): JSX.Element => (
        <PackageCell original={row.original} t={t} />
      ),
      header: t('Package'),
      id: 'license.package',
      size: 200,
      sortDescFirst: false,
    },
    {
      cell: ({ row }: CellContext<Account, unknown>): JSX.Element => (
        <CsmCell original={row.original} />
      ),
      enableSorting: false,
      header: t('Account Manager'),
      id: 'csm',
      size: 200,
    },
  ];
}

export default accountsColumns;
